<template>
	<NuxtLayout name="one-column-layout">
		<template #title>{{ $t(`homepageHeading_${productConfig.i18nKeyPlural}`) }}</template>

		<template #subtitle>
			<div v-html="$t(`homepageBody_${productConfig.i18nKeyPlural}`)"></div>

			<div
				v-if="productPage.previews && productPage.previews.length"
				class="flex gap-4 justify-center">
				<template v-for="preview in productPage.previews">
					<LazyProductPreviewMapLink
						:link-title="t(preview.linkTitle)"
						:title="t(preview.popupTitle)"
						:image-src="preview.image" />
				</template>
			</div>

			<a
				v-if="shouldShowSeatingPreview"
				href="#"
				class="underline"
				@click="openSeatPreview">
				{{ $t("seatsIoPreviewLink") }}
			</a>
			<ProductSeatPreview
				v-if="shouldShowSeatingPreview"
				:seats-io-workspace-key="seatsIOConfig.PUBLIC_WORKSPACE_KEY"
				:seats-io-event="seatsIOConfig.EVENT_ID" />
		</template>

		<template #contentSlot1>
			<StepperWidget />
		</template>

		<template #contentSlot2>
			<ClientOnly>
				<TransitionGroup name="slide-fade">
					<ProductCard
						v-for="product in productData"
						:id="product.id"
						:key="product.id"
						:title="product.title"
						:description="product.description"
						:short-description="product.shortDescription"
						:price="product.price"
						:images="product.images"
						:available-quantity="product.availableQuantity"
						:is-quantity-in-carts="product.isQuantityInCarts"
						:sold-out-link="productConfig.soldOutLink"
						:all-products-sold-out="allProductsSoldOut"
						:all-products-sold-out-link="productConfig.soldOutSecondaryLink"
						class="" />
				</TransitionGroup>

				<CardTernary
					class="container flex justify-center mx-auto p-10 productCardColors productCardComponent"
					v-if="isSlowNetwork && isFetchingCurrentProduct">
					<p class="font-serif text-primary text-2xl lg:text-4xl mt-2 customCardTitle">
						{{ $t("fetchingProducts") }}
					</p>
				</CardTernary>
			</ClientOnly>
		</template>
	</NuxtLayout>
</template>

<script setup>
	import { navigateTo, onBeforeRouteLeave } from "#app"
	import { storeToRefs } from "pinia"
	import { useProductStore } from "~/stores/ProductStore"
	import { useCartStore } from "~/stores/CartStore"
	import { SHOW_ERROR } from "assets/events/errors.js"
	import { useSettingsStore } from "~/stores/SettingsStore"
	import { SHOW_SEAT_PREVIEW_POPUP } from "assets/events/cart.js"

	const { $productsService, $eventBus, $seatSelector } = useNuxtApp()
	const { t } = useI18n()

	const path = useRoute().path
	const runtimeConfig = useRuntimeConfig()

	const settingsStore = useSettingsStore()
	const { isSlowNetwork, isProductFetchInProgress } = storeToRefs(settingsStore)

	//Check that the product type viewed is valid with valid config. Otherwise, redirect to 404 page.
	const productPage = runtimeConfig.public.PAGES.find((page) => page.urlPath === path)
	if (!productPage) {
		navigateTo({
			path: "/notFound",
		})
	}
	const productConfig = runtimeConfig.public.PRODUCTS[productPage.productType]

	//Set the Page Metadata
	useHead(() => {
		return {
			title: t(productConfig.i18nKeyPlural),
			meta: [
				{
					name: "description",
					content: `Buy ${t(productConfig.i18nKeyPlural)} for ${runtimeConfig.public.EVENT_NAME}`,
				},
			],
		}
	})

	const isFetchingCurrentProduct = computed(() => {
		return isProductFetchInProgress.value[productConfig.productTypeId] ?? false
	})

	//Fetch the products from the product store
	const productStore = useProductStore()
	const { products } = storeToRefs(productStore)
	const productData = ref([])
	const allProductsSoldOut = computed(() =>
		productData.value.every((product) => product.availableQuantity <= 0 && product.isQuantityInCarts === false)
	)

	watch(
		products,
		() => {
			productData.value = productStore.getProductsOfType(productConfig.productTypeId)
		},
		{ deep: true }
	)

	onMounted(() => {
		nextTick(() => {
			$productsService.$getProductsByType(productConfig.productTypeId)
		})
	})

	//Check if the user has no tickets
	const cartStore = useCartStore()
	onBeforeRouteLeave((to, from, next) => {
		if (to.path.indexOf("login") >= 0) {
			next()
			return
		}

		//If on the tickets page, confirm with the user that they have no tickets
		let shouldShowNoTicketsPopup =
			runtimeConfig.public.SHOW_TICKET_REQUIRED_POPUP && !settingsStore.userIsBeingRedirected
		shouldShowNoTicketsPopup &= from.path.indexOf("/tickets") >= 0
		shouldShowNoTicketsPopup &= cartStore.getNumProductsByType(runtimeConfig.public.PRODUCTS.ticket.productTypeId) <= 0

		if (shouldShowNoTicketsPopup) {
			$eventBus.$emit(SHOW_ERROR, {
				titleText: t("noTicketsPopupTitle"),
				bodyText: t("noTicketsPopupBody"),
				closeBtnText: t("stay"),
				acceptBtnText: t("continue"),
				acceptCallback: next,
			})
		} else {
			next()
		}
	})

	// Load Seats Reservation Popup as a global component. Opted for this design so that the use can view/edit their selection from any
	// page (no need to redirect back to lodging). However, this architecture only allows for one reservation chart sitewide instead of per product.
	const seatsIOConfig = runtimeConfig.public.SEATS_IO
	const shouldShowSeatingPreview =
		seatsIOConfig &&
		seatsIOConfig.SEATING_PREVIEW &&
		seatsIOConfig.SEATING_PREVIEW.ENABLED &&
		seatsIOConfig.SEATING_PREVIEW.SHOW_ON_PRODUCT_TYPE_ID === productConfig.productTypeId

	const openSeatPreview = () => {
		$eventBus.$emit(SHOW_SEAT_PREVIEW_POPUP)
	}
</script>

<style lang="scss" scoped></style>
